// src/components/Layout.js

import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { Box, CssBaseline } from '@mui/material';
import { Outlet } from 'react-router-dom';
import CreatorSidebar, { collapsedWidth } from './CreatorSidebar';
import CustomerSidebar from './CustomerSidebar';
import StudentSidebar from './StudentSidebar';
import TradingSidebar from './TradingSidebar';
import FinanceSidebar from './FinanceSidebar';
import Footer from './Footer';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { SETUP_COMPLETE_LOCAL_KEY } from "../config";
import { PanelProvider, usePanel, PanelType } from '../contexts/PanelContext';
import { CompanySettingsProvider } from '../contexts/CompanySettingsContext';

const drawerWidth = 240;

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// Inner layout component that uses PanelContext
function LayoutContent() {
    const navigate = useNavigate();
    const theme = useTheme();
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMobileView, setIsMobileView] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Get panel context
    const { activePanel, isLoading: panelLoading } = usePanel();

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const parsedUser = JSON.parse(userStr);
                setUserData(parsedUser);
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('user');
                navigate('/Login');
            }
        } else {
            navigate('/Login');
        }
        setIsLoading(false);
    }, [navigate]);

    useEffect(() => {
        if (!userData || isLoading) return;
        console.log("Checking auth and subscription");

        const checkAuthAndSubscription = async () => {
            try {
                const subPromise = axiosInstance.get(`/subscriptions/current`);
                const [subResult] = await Promise.allSettled([subPromise]);
                let fetchedSubscription = null;

                if (subResult.status === 'fulfilled' && subResult.value?.data) {
                    fetchedSubscription = subResult.value.data.data || subResult.value.data;
                    if (!fetchedSubscription || !fetchedSubscription.plan) {
                        if (fetchedSubscription?.status !== 'freemium') {
                            fetchedSubscription = null;
                        }
                    }
                } else if (subResult.reason?.response?.status === 404) {
                    const responseData = subResult.reason.response.data;
                    fetchedSubscription = responseData?.data?.plan?.name === 'freemium' ? responseData.data : null;
                } else {
                    console.error("Subscription fetch failed:", subResult.reason);
                }

                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const prevUser = JSON.parse(userStr);
                    const updatedUser = {
                        ...prevUser,
                        active_team: {
                            ...prevUser.active_team,
                            subscription: fetchedSubscription,
                        },
                        teams: prevUser.teams.map(team =>
                            team.team_id === prevUser.active_team.team_id
                                ? { ...team, subscription: fetchedSubscription }
                                : team
                        ),
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }

                if (!fetchedSubscription) {
                    localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
                    navigate('/SignUp-Process-Steps');
                }
            } catch (err) {
                console.error("Unexpected error during subscription fetch:", err);
            }
        };

        checkAuthAndSubscription();
    }, [userData?.access_token, isLoading, navigate]);

    useEffect(() => {
        setIsMobileView(isMobile);
        setIsSidebarOpen(!isMobile);
    }, [isMobile]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleMobileSidebarClose = () => {
        if (isMobileView) {
            setIsSidebarOpen(false);
        }
    };

    if (!userData) {
        return null;
    }

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');`}</style>
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                fontFamily,
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)',
                overflow: 'hidden',
            }}>
                <CssBaseline />
                <Header
                    toggleSidebar={toggleSidebar}
                    isSidebarCollapsed={!isSidebarOpen}
                    isMobile={isMobileView}
                />
                {/* Render appropriate sidebar based on active panel */}
                {activePanel === PanelType.FINANCE ? (
                    <FinanceSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : activePanel === PanelType.TRADING ? (
                    <TradingSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : activePanel === PanelType.STUDENT ? (
                    <StudentSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : activePanel === PanelType.CUSTOMER ? (
                    <CustomerSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : (
                    <CreatorSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                )}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100vh',
                        width: {
                            xs: '100%',
                            md: isSidebarOpen
                                ? `calc(100% - ${drawerWidth}px)`
                                : `calc(100% - ${collapsedWidth}px)`
                        },
                        ml: {
                            xs: 0,
                            md: isSidebarOpen ? `${drawerWidth}px` : `${collapsedWidth}px`
                        },
                        transition: 'margin-left 0.3s ease, width 0.3s ease',
                    }}
                >
                    {/* Content Area */}
                    <Box sx={{
                        flexGrow: 1,
                        p: { xs: 1.5, sm: 2, md: 3 },
                        pt: { xs: 8, sm: 9, md: 10 },
                        overflowX: 'hidden',
                    }}>
                        <Outlet />
                    </Box>

                    {/* Footer */}
                    <Box sx={{ p: 2 }}>
                        <Footer />
                    </Box>
                </Box>
            </Box>
        </>
    );
}

// Wrapper component that provides PanelContext
function Layout() {
    return (
        <PanelProvider>
            <CompanySettingsProvider>
                <LayoutContent />
            </CompanySettingsProvider>
        </PanelProvider>
    );
}

export default Layout;
